import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Mail, MessageCircle, Phone, Search, Filter, RefreshCw,
  Eye, Trash2, X, Check, ExternalLink, Clock, UserPlus, FileText, Edit3,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import AdminLeadEditModal from './AdminLeadEditModal';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

// =============================================================
// CONSTANTS
// =============================================================
const STATUS_OPTIONS = [
  { value: 'in_progress', label: '⏳ In corso', badge: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'new', label: '🆕 Nuovo', badge: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'contacted', label: '📞 Contattato', badge: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'qualified', label: '✅ Qualificato', badge: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'converted', label: '💎 Convertito', badge: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'lost', label: '❌ Perso', badge: 'bg-red-100 text-red-800 border-red-300' },
];

const STATUS_MAP = STATUS_OPTIONS.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});

const STEP_LABELS = {
  1: 'Contatto',
  2: 'Settore',
  3: 'Destinazione',
  4: 'Prodotti',
  5: 'Formati',
  6: 'Logo',
  7: 'Conferma',
};

// =============================================================
// HELPERS
// =============================================================
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const daysAgo = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

const cleanPhone = (p) => (p || '').replace(/[^0-9]/g, '');

const buildWhatsAppLink = (phone, message) => {
  const num = cleanPhone(phone);
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(message || '')}`;
};

const buildMailto = (email, subject, body) => {
  if (!email) return null;
  return `mailto:${email}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`;
};

const customerName = (l) => {
  if (l.tipoCliente === 'azienda') return l.ragioneSociale || '—';
  return `${l.nome || ''} ${l.cognome || ''}`.trim() || '—';
};

const productSummary = (l) => {
  if (!l.prodotti || l.prodotti.length === 0) return '—';
  return l.prodotti.map((p) => {
    const base = (p.categoryId === 'altro' && p.customName) ? `Altro: ${p.customName}` : (p.categoryId || '—');
    const mode = p.recipeMode === 'custom' ? ' (custom)' : '';
    return `${base}${mode}`;
  }).join(', ');
};

// =============================================================
// MAIN COMPONENT
// =============================================================
const AdminLandingLeads = () => {
  usePageTitle('Landing Leads - Admin');
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailLead, setDetailLead] = useState(null);
  const [deleteLeadId, setDeleteLeadId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const loadLeads = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${adminToken}` };
      const res = await fetch(`${apiUrl}/api/admin/landing-leads?limit=500`, { headers });
      if (res.ok) {
        const d = await res.json();
        setLeads(d.leads || []);
      } else {
        toast({ title: 'Errore', description: 'Impossibile caricare i lead', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Errore lead:', e);
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [adminToken, apiUrl, toast]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  // Filtering
  const filtered = useMemo(() => {
    let list = leads;
    if (statusFilter !== 'all') list = list.filter((l) => (l.status || 'new') === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) => {
        const blob = [
          customerName(l), l.email, l.telefono, l.quoteRef, l.ragioneSociale,
          l.settori?.join(' '), l.destinazione, productSummary(l),
        ].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    return list;
  }, [leads, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: leads.length };
    STATUS_OPTIONS.forEach((s) => { c[s.value] = 0; });
    leads.forEach((l) => {
      const st = l.status || 'new';
      if (c[st] != null) c[st] += 1;
    });
    return c;
  }, [leads]);

  const updateStatus = async (leadId, newStatus) => {
    setStatusUpdating(leadId);
    try {
      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${leadId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast({ title: 'Stato aggiornato', description: `Lead marcato come ${STATUS_MAP[newStatus]?.label || newStatus}` });
        setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
      } else {
        toast({ title: 'Errore', description: 'Aggiornamento fallito', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setStatusUpdating(null);
    }
  };

  const deleteLead = async (leadId) => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${leadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        setLeads((prev) => prev.filter((l) => l.id !== leadId));
        toast({ title: 'Lead eliminato' });
      } else {
        toast({ title: 'Errore', description: 'Eliminazione fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setDeleteLeadId(null);
    }
  };

  const [converting, setConverting] = useState(null); // {leadId, mode: 'customer'|'quote'}
  const [convertResult, setConvertResult] = useState(null);
  const [editLead, setEditLead] = useState(null); // lead da modificare nel modal admin

  const handleLeadSaved = (updatedLead) => {
    if (!updatedLead) return;
    setLeads((prev) => prev.map((l) => (l.id === updatedLead.id ? { ...l, ...updatedLead } : l)));
    if (detailLead?.id === updatedLead.id) setDetailLead((d) => ({ ...d, ...updatedLead }));
    if (editLead?.id === updatedLead.id) setEditLead((d) => ({ ...d, ...updatedLead }));
  };

  const convertToCustomer = async (leadId) => {
    setConverting({ leadId, mode: 'customer' });
    try {
      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${leadId}/convert-customer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({
          title: data.created_new ? '✅ Nuovo cliente creato' : '🔗 Cliente esistente collegato',
          description: `Il lead è stato qualificato. Customer ID: ${data.customer_id.slice(0, 8)}...`,
        });
        setLeads((prev) => prev.map((l) => (
          l.id === leadId ? { ...l, customer_id: data.customer_id, status: data.lead_status } : l
        )));
        setConvertResult({ leadId, mode: 'customer', customer_id: data.customer_id, created_new: data.created_new });
      } else {
        toast({ title: 'Errore', description: data.detail || 'Conversione fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setConverting(null);
    }
  };

  const convertToQuote = async (leadId) => {
    setConverting({ leadId, mode: 'quote' });
    try {
      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${leadId}/convert-quote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({
          title: '✅ Preventivo creato',
          description: `${data.quote_number} con ${data.items_count} righe. ${data.customer_created_new ? 'Cliente nuovo creato.' : 'Cliente esistente collegato.'}`,
        });
        setLeads((prev) => prev.map((l) => (
          l.id === leadId ? { ...l, customer_id: data.customer_id, quote_id: data.quote_id, quote_number: data.quote_number, status: 'converted' } : l
        )));
        setConvertResult({
          leadId, mode: 'quote',
          quote_id: data.quote_id,
          quote_number: data.quote_number,
          customer_id: data.customer_id,
        });
      } else {
        toast({ title: 'Errore', description: data.detail || 'Conversione fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setConverting(null);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-purple-600" />
              Landing Leads — /preventivo
            </h1>
            <p className="text-sm text-gray-500 mt-1">Contatti generati dalla landing pubblica di qualifica B2B</p>
          </div>
          <button
            onClick={loadLeads}
            disabled={loading}
            data-testid="landing-leads-refresh-btn"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Ricarica
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard
            label="Totali"
            value={counts.all}
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
            testid="filter-all"
            color="bg-gray-100 text-gray-800 border-gray-300"
          />
          {STATUS_OPTIONS.map((s) => (
            <StatCard
              key={s.value}
              label={s.label}
              value={counts[s.value] || 0}
              active={statusFilter === s.value}
              onClick={() => setStatusFilter(s.value)}
              testid={`filter-${s.value}`}
              color={s.badge}
            />
          ))}
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, email, ragione sociale, telefono, riferimento..."
                data-testid="landing-leads-search"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Filter className="w-4 h-4" />
              <span>{filtered.length} {filtered.length === 1 ? 'lead' : 'leads'}</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
              Caricamento...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              <Sparkles className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="font-medium">Nessun lead trovato</p>
              <p className="text-sm mt-1">I lead generati dalla landing <code className="text-purple-600">/preventivo</code> appariranno qui.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Stato</th>
                    <th className="px-4 py-3 text-left">Step</th>
                    <th className="px-4 py-3 text-left">Cliente</th>
                    <th className="px-4 py-3 text-left">Contatti</th>
                    <th className="px-4 py-3 text-left">Prodotti</th>
                    <th className="px-4 py-3 text-left">Quantità</th>
                    <th className="px-4 py-3 text-left">Rif.</th>
                    <th className="px-4 py-3 text-left">Quando</th>
                    <th className="px-4 py-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((l) => {
                    const status = l.status || 'new';
                    const statusInfo = STATUS_MAP[status] || STATUS_MAP.new;
                    const days = daysAgo(l.created_at);
                    return (
                      <tr key={l.id} className="hover:bg-purple-50/30" data-testid={`lead-row-${l.id}`}>
                        <td className="px-4 py-3">
                          <select
                            value={status}
                            disabled={statusUpdating === l.id}
                            onChange={(e) => updateStatus(l.id, e.target.value)}
                            data-testid={`lead-status-select-${l.id}`}
                            className={`text-xs font-medium px-2 py-1 rounded-full border ${statusInfo.badge} cursor-pointer`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          {l.last_step ? (
                            <div className="flex flex-col gap-1">
                              <span className="text-xs font-bold text-gray-800">
                                {status === 'in_progress' ? '🔴' : '✓'} {l.last_step}/7
                              </span>
                              <span className="text-[10px] text-gray-500">{STEP_LABELS[l.last_step] || ''}</span>
                              {status === 'in_progress' && (
                                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                  <div className="h-full bg-orange-400" style={{ width: `${(l.last_step / 7) * 100}%` }} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{customerName(l)}</div>
                          {l.tipoCliente && (
                            <div className="text-xs text-gray-500">{l.tipoCliente === 'azienda' ? '🏢 Azienda' : '👤 Privato'}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-700 break-all">{l.email || '—'}</div>
                          <div className="text-xs text-gray-500">{l.telefono || '—'}</div>
                        </td>
                        <td className="px-4 py-3 max-w-[240px]">
                          <div className="text-xs text-gray-700 truncate" title={productSummary(l)}>{productSummary(l)}</div>
                          {l.formati?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-0.5">📦 {l.formati.join(' • ')}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700">
                          {l.quantita ? `${l.quantita} btg` : '—'}
                          {l.fascia && <div className="text-xs text-gray-500 capitalize">{l.fascia}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-purple-700">{l.quoteRef || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {fmtDate(l.created_at)}
                          {days != null && (
                            <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" /> {days === 0 ? 'oggi' : `${days}gg fa`}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <IconBtn
                              title="Dettaglio"
                              onClick={() => setDetailLead(l)}
                              testid={`lead-detail-${l.id}`}
                              className="text-purple-600 hover:bg-purple-50"
                            >
                              <Eye className="w-4 h-4" />
                            </IconBtn>
                            <IconBtn
                              title="Completa/Modifica con cliente"
                              onClick={() => setEditLead(l)}
                              testid={`lead-edit-${l.id}`}
                              className="text-fuchsia-600 hover:bg-fuchsia-50"
                            >
                              <Edit3 className="w-4 h-4" />
                            </IconBtn>
                            {!l.customer_id && (
                              <IconBtn
                                title="Crea/Collega Cliente CRM"
                                onClick={() => convertToCustomer(l.id)}
                                testid={`lead-convert-customer-${l.id}`}
                                className="text-indigo-600 hover:bg-indigo-50 disabled:opacity-40"
                                disabled={converting?.leadId === l.id}
                              >
                                <UserPlus className="w-4 h-4" />
                              </IconBtn>
                            )}
                            {!l.quote_id && (l.prodotti?.length > 0) && (
                              <IconBtn
                                title="Genera Preventivo"
                                onClick={() => convertToQuote(l.id)}
                                testid={`lead-convert-quote-${l.id}`}
                                className="text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                                disabled={converting?.leadId === l.id}
                              >
                                <FileText className="w-4 h-4" />
                              </IconBtn>
                            )}
                            {l.quote_id && (
                              <Link
                                to={`/admin/quotes/${l.quote_id}`}
                                title={`Apri preventivo ${l.quote_number || ''}`}
                                data-testid={`lead-quote-link-${l.id}`}
                                className="p-1.5 rounded text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Link>
                            )}
                            {l.email && (
                              <a
                                href={buildMailto(l.email, `Richiesta preventivo ${l.quoteRef || ''} - IlTuoAmaro.it`, `Buongiorno ${customerName(l)},\n\ngrazie per aver inviato la tua richiesta dalla nostra landing. Ho ricevuto i dettagli e sto preparando l'offerta personalizzata per te.\n\nA presto,\nDavide — IlTuoAmaro.it`)}
                                title="Email"
                                data-testid={`lead-email-${l.id}`}
                                className="p-1.5 rounded text-blue-600 hover:bg-blue-50"
                              >
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                            {l.telefono && buildWhatsAppLink(l.telefono, `Ciao ${customerName(l)}! Sono Davide di IlTuoAmaro.it. Ho ricevuto la tua richiesta dalla landing (rif. ${l.quoteRef || ''}) e ti sto preparando un'offerta su misura 😊`) && (
                              <a
                                href={buildWhatsAppLink(l.telefono, `Ciao ${customerName(l)}! Sono Davide di IlTuoAmaro.it. Ho ricevuto la tua richiesta dalla landing (rif. ${l.quoteRef || ''}) e ti sto preparando un'offerta su misura 😊`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="WhatsApp"
                                data-testid={`lead-whatsapp-${l.id}`}
                                className="p-1.5 rounded text-green-600 hover:bg-green-50"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </a>
                            )}
                            <IconBtn
                              title="Elimina"
                              onClick={() => setDeleteLeadId(l.id)}
                              testid={`lead-delete-${l.id}`}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </IconBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detailLead && (
        <LeadDetailModal
          lead={leads.find((l) => l.id === detailLead.id) || detailLead}
          adminToken={adminToken}
          apiUrl={apiUrl}
          toast={toast}
          onClose={() => setDetailLead(null)}
          onEdit={() => { setEditLead(leads.find((l) => l.id === detailLead.id) || detailLead); }}
          onConvertCustomer={() => convertToCustomer(detailLead.id)}
          onConvertQuote={() => convertToQuote(detailLead.id)}
          converting={converting?.leadId === detailLead.id ? converting.mode : null}
        />
      )}

      {/* Edit Modal (Completa con cliente) */}
      {editLead && (
        <AdminLeadEditModal
          lead={leads.find((l) => l.id === editLead.id) || editLead}
          adminToken={adminToken}
          apiUrl={apiUrl}
          onClose={() => setEditLead(null)}
          onSaved={handleLeadSaved}
          onConvertQuote={async () => {
            await convertToQuote(editLead.id);
            setEditLead(null);
          }}
        />
      )}

      {/* Delete Confirm */}
      {deleteLeadId && (
        <ConfirmModal
          title="Eliminare questo lead?"
          message="L'azione è irreversibile. Tutti i dati raccolti dalla landing per questo contatto verranno persi."
          onConfirm={() => deleteLead(deleteLeadId)}
          onCancel={() => setDeleteLeadId(null)}
        />
      )}
    </AdminLayout>
  );
};

// =============================================================
// SUB COMPONENTS
// =============================================================
function StatCard({ label, value, active, onClick, testid, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'shadow-md scale-105 ' + color : 'border-gray-200 bg-white hover:border-gray-300'}`}
    >
      <div className="text-xs text-gray-500 truncate">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </button>
  );
}

function IconBtn({ children, title, onClick, testid, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <h4 className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-2 border-b border-purple-100 pb-1">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ label, value }) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 text-sm">
      <div className="font-medium text-gray-600 sm:w-40">{label}</div>
      <div className="text-gray-900 flex-1 break-words">{Array.isArray(value) ? value.join(', ') : value}</div>
    </div>
  );
}

function LeadDetailModal({ lead, adminToken, apiUrl, toast, onClose, onEdit, onConvertCustomer, onConvertQuote, converting }) {
  const status = lead.status || 'new';
  const statusInfo = STATUS_MAP[status] || STATUS_MAP.new;
  const hasProducts = (lead.prodotti || []).length > 0;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-detail-modal"
      >
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-500 text-white p-5 rounded-t-xl flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold">{customerName(lead)}</h3>
            <p className="text-xs opacity-90 mt-0.5">{lead.email}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusInfo.badge}`}>
                {statusInfo.label}
              </span>
              {lead.quoteRef && (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-mono bg-white/20 text-white">
                  {lead.quoteRef}
                </span>
              )}
              {lead.last_step && (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-white/20 text-white">
                  📍 Step {lead.last_step}/7 ({STEP_LABELS[lead.last_step] || ''})
                </span>
              )}
              {lead.customer_id && (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-400/30 text-white">
                  👤 Cliente collegato
                </span>
              )}
              {lead.quote_id && (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-yellow-400/30 text-white">
                  📄 {lead.quote_number || 'Preventivo creato'}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded" data-testid="lead-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* CONVERSION ACTION BAR */}
        <div className="p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border-b border-purple-100">
          <div className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-2">⚡ Azioni di conversione</div>
          <div className="flex flex-wrap gap-2">
            {onEdit && (
              <button
                onClick={onEdit}
                data-testid="modal-edit-btn"
                className="px-4 py-2 bg-fuchsia-600 text-white rounded-lg text-sm font-semibold hover:bg-fuchsia-700 flex items-center gap-2"
              >
                <Edit3 className="w-4 h-4" />
                Completa con cliente
              </button>
            )}
            {!lead.customer_id ? (
              <button
                onClick={onConvertCustomer}
                disabled={converting !== null}
                data-testid="modal-convert-customer-btn"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {converting === 'customer' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {converting === 'customer' ? 'Creazione...' : 'Crea/Collega Cliente CRM'}
              </button>
            ) : (
              <Link
                to={`/admin/customers/${lead.customer_id}`}
                data-testid="modal-customer-link"
                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-200 flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Apri scheda cliente
              </Link>
            )}

            {!lead.quote_id ? (
              <button
                onClick={onConvertQuote}
                disabled={converting !== null || !hasProducts}
                data-testid="modal-convert-quote-btn"
                title={!hasProducts ? 'Lead senza prodotti' : ''}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              >
                {converting === 'quote' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {converting === 'quote' ? 'Creazione...' : 'Genera Preventivo (DRAFT)'}
              </button>
            ) : (
              <Link
                to={`/admin/quotes/${lead.quote_id}`}
                data-testid="modal-quote-link"
                className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-semibold hover:bg-emerald-200 flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Apri preventivo {lead.quote_number || ''}
              </Link>
            )}
          </div>
          {!lead.customer_id && !lead.quote_id && (
            <p className="text-xs text-gray-500 mt-2">
              💡 <strong>Cliente</strong> = crea/collega l&apos;utente CRM (idempotente per email).
              <strong> Preventivo</strong> = crea anche il cliente e genera un draft con i prodotti del lead (i prezzi li imposti dopo).
            </p>
          )}
        </div>

        <div className="p-5">
          <Section title="Contatto">
            <KV label="Tipo cliente" value={lead.tipoCliente === 'azienda' ? '🏢 Azienda' : lead.tipoCliente === 'privato' ? '👤 Privato' : null} />
            <KV label="Ragione sociale" value={lead.ragioneSociale} />
            <KV label="Nome" value={lead.nome} />
            <KV label="Cognome" value={lead.cognome} />
            <KV label="Email" value={lead.email} />
            <KV label="Telefono" value={lead.telefono} />
          </Section>

          <Section title="Profilo business">
            <KV label="Settori" value={lead.settori} />
            {lead.settoreAltro && <KV label="Altro settore" value={lead.settoreAltro} />}
            <KV label="Destinazione" value={lead.destinazione} />
            {lead.destinazioneAltro && <KV label="Altra destinazione" value={lead.destinazioneAltro} />}
          </Section>

          <Section title={`Prodotti richiesti (${lead.prodotti?.length || 0})`}>
            {(lead.prodotti || []).map((p, idx) => (
              <div key={idx} className="rounded-lg border border-purple-100 bg-purple-50/30 p-3 text-sm">
                <div className="font-semibold text-purple-900 mb-1">
                  {idx + 1}. {p.categoryId === 'altro' && p.customName ? `Altro — ${p.customName}` : p.categoryId}
                </div>
                <KV label="Modalità" value={p.recipeMode === 'custom' ? '⚗️ Personalizzata' : '✓ Collaudata'} />
                <KV label="Ricetta" value={p.ricettaScelta} />
                {p.recipeMode === 'custom' && (
                  <>
                    <KV label="Gradazione" value={p.gradazione?.range ? `${p.gradazione.range}${p.gradazione.precise ? ` (${p.gradazione.precise}%)` : ''}` : null} />
                    <KV
                      label="Botaniche"
                      value={p.botaniche?.length > 0 ? p.botaniche.map((b) => `${b.name} (${b.intensity}/5)`).join(', ') : null}
                    />
                    <KV label="Note ricetta" value={p.noteRicetta} />
                  </>
                )}
              </div>
            ))}
          </Section>

          <Section title="Formato & Quantità">
            <KV label="Formati" value={lead.formati} />
            <KV label="Fascia" value={lead.fascia} />
            <KV label="Quantità" value={lead.quantita ? `${lead.quantita} bottiglie` : null} />
          </Section>

          <Section title="Branding & Note">
            <KV label="Logo" value={lead.logoMode} />
            {lead.logoFileName && <KV label="File logo" value={lead.logoFileName} />}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {lead.hasLogoFile && (
                <button
                  type="button"
                  data-testid="download-logo-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${lead.id}/logo`, {
                        headers: { Authorization: `Bearer ${adminToken}` },
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({}));
                        throw new Error(err.detail || `HTTP ${res.status}`);
                      }
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = lead.logoFileName || `logo-${lead.id}`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast({ title: 'Logo scaricato' });
                    } catch (e) {
                      toast({ title: 'Errore download', description: String(e.message || e), variant: 'destructive' });
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-purple-600 text-white hover:bg-purple-700"
                >
                  ⬇️ Scarica logo cliente
                </button>
              )}
              {lead.hasQuotePdf && (
                <button
                  type="button"
                  data-testid="download-quote-pdf-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${lead.id}/quote-pdf`, {
                        headers: { Authorization: `Bearer ${adminToken}` },
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({}));
                        throw new Error(err.detail || `HTTP ${res.status}`);
                      }
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Preventivo_${lead.quoteRef || lead.id}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast({ title: 'PDF preventivo scaricato' });
                    } catch (e) {
                      toast({ title: 'Errore download', description: String(e.message || e), variant: 'destructive' });
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                >
                  📄 Scarica PDF preventivo cliente
                </button>
              )}
            </div>
            <KV label="Branding service" value={lead.brandingService ? '✓ Richiesto' : null} />
            <KV label="Note grafico" value={lead.noteGrafico} />
            <KV label="Note finali" value={lead.noteFinali} />
          </Section>

          {/* Offerta auto-calcolata dal cliente */}
          {lead.pricing_snapshot && (
            <PricingSnapshotSection snapshot={lead.pricing_snapshot} />
          )}

          <Section title="Meta">
            <KV label="Step raggiunto" value={lead.last_step ? `${lead.last_step}/7 — ${STEP_LABELS[lead.last_step] || ''}` : null} />
            <KV label="Creato" value={fmtDate(lead.created_at)} />
            <KV label="Aggiornato" value={fmtDate(lead.updated_at)} />
            <KV label="Completato" value={fmtDate(lead.completed_at)} />
            <KV label="Sorgente" value={lead.source || 'landing-preventivo'} />
            <KV label="Draft ID" value={lead.draft_id} />
            {lead.admin_note && <KV label="Note admin" value={lead.admin_note} />}
          </Section>

          {/* Quick actions */}
          <div className="border-t border-gray-200 pt-4 flex flex-wrap gap-2">
            {lead.email && (
              <a
                href={buildMailto(lead.email, `Richiesta preventivo ${lead.quoteRef || ''} - IlTuoAmaro.it`, `Buongiorno ${customerName(lead)},\n\ngrazie per aver inviato la tua richiesta dalla nostra landing. Ho ricevuto i dettagli e sto preparando l'offerta personalizzata per te.\n\nA presto,\nDavide — IlTuoAmaro.it`)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5"
              >
                <Mail className="w-4 h-4" /> Email
              </a>
            )}
            {lead.telefono && buildWhatsAppLink(lead.telefono, `Ciao ${customerName(lead)}! Sono Davide di IlTuoAmaro.it. Ho ricevuto la tua richiesta dalla landing (rif. ${lead.quoteRef || ''}) e ti sto preparando un'offerta su misura 😊`) && (
              <a
                href={buildWhatsAppLink(lead.telefono, `Ciao ${customerName(lead)}! Sono Davide di IlTuoAmaro.it. Ho ricevuto la tua richiesta dalla landing (rif. ${lead.quoteRef || ''}) e ti sto preparando un'offerta su misura 😊`)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1.5"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {lead.telefono && (
              <a
                href={`tel:${cleanPhone(lead.telefono)}`}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-1.5"
              >
                <Phone className="w-4 h-4" /> Chiama
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-5" data-testid="confirm-modal">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">Annulla</button>
          <button onClick={onConfirm} data-testid="confirm-ok" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminLandingLeads;

// ============================================================
// PricingSnapshotSection — Mostra il preventivo auto-calcolato dal cliente
// ============================================================
function PricingSnapshotSection({ snapshot }) {
  if (!snapshot || !snapshot.items) return null;
  const fmt = (n) => n == null ? '—' : `€${Number(n).toFixed(2)}`;
  return (
    <div className="mb-5" data-testid="pricing-snapshot-section">
      <h4 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2">
        💰 Offerta auto-calcolata dal cliente
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-fuchsia-100 text-fuchsia-700 normal-case tracking-normal">
          {snapshot.total_bottles || 0} bottiglie totali
        </span>
      </h4>

      <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-lg border border-purple-200 p-3">
        {snapshot.items.map((it, idx) => (
          <div key={idx} className="mb-3 last:mb-0 bg-white rounded-md p-3 border border-purple-100" data-testid={`pricing-snapshot-item-${idx}`}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
              <div className="font-semibold text-gray-900">
                {it.category_label}
                {it.recipe_label && <span className="text-gray-500 font-normal"> — {it.recipe_label}</span>}
                {it.alcohol_pct != null && (
                  <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 align-middle">
                    {Number(it.alcohol_pct).toFixed(0)}% vol
                  </span>
                )}
              </div>
              {!it.priceable && (
                <span className="text-[11px] italic text-amber-700">Prezzo da definire</span>
              )}
            </div>

            {(it.formats || []).length > 0 && (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 font-medium">Formato</th>
                    <th className="py-1 font-medium text-right">Quantità</th>
                    <th className="py-1 font-medium">Scaglione</th>
                    <th className="py-1 font-medium text-right">Prezzo unit.</th>
                    <th className="py-1 font-medium text-right">Totale riga</th>
                  </tr>
                </thead>
                <tbody>
                  {it.formats.map((fr, j) => (
                    <tr key={j} className="border-b border-gray-100 last:border-b-0">
                      <td className="py-1.5">{fr.format}</td>
                      <td className="py-1.5 text-right font-mono">{fr.quantity}</td>
                      <td className="py-1.5 text-gray-600">{fr.tier_label || '—'}{fr.discount_pct > 0 && <span className="text-fuchsia-700 font-semibold"> (-{fr.discount_pct}%)</span>}</td>
                      <td className="py-1.5 text-right font-mono">{fmt(fr.unit_price)}</td>
                      <td className="py-1.5 text-right font-mono font-semibold">{fmt(fr.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-purple-200 mt-2">
          <span className="text-sm font-semibold text-gray-700">TOTALE OFFERTA (indicativo)</span>
          <span className="text-lg font-bold text-fuchsia-700 font-mono" data-testid="pricing-snapshot-total">
            {fmt(snapshot.total_amount)}
          </span>
        </div>

        <p className="text-[10px] text-gray-500 italic mt-2">
          ⚠️ Prezzi indicativi auto-calcolati dal cliente sul listino /preventivo. Il preventivo definitivo va emesso dall&apos;admin.
        </p>
      </div>
    </div>
  );
}
